// Shopify sell items and the Parts library (materials, sub-assemblies, services): one table over the item table, filtered
// by kind. Manufacturing projects --- Sell items filter General / Built (built: has a BOM or is in a project) with a
// project chip; the Parts library filters by kind (?kind=) and project (?project=) and shows where each part is used.
// Packaging --- packaging parts are their own view (?kind=packaging, by Category ?pack=), with what each one is and the
// state of its print files; they're made with NewPackagingDialog and their panel has a Packaging section.
// A part's Type and Category (shared/part-type.ts): Type is Material, Packaging, Sub-assembly or Service, and its
// Category's choices follow it; stored as kind + part_type (+ pack_type for packaging), never shown as those.
import { Archive, Boxes, Check, ExternalLink, FilePlus2, ImagePlus, List, ListTree, Pencil, Star, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { calc } from "../../../shared/calc.ts";
import type { Item, VendorItem } from "../../../shared/db.gen.ts";
import { groupSummary, isCurrent, memberLabel, SHARED_FILE_LABELS, skuStem } from "../../../shared/part-groups.ts"; // Part groups ---
import { skuPrefix } from "../../../shared/part-sku.ts";
import {
  categoryLabel,
  categoryOf,
  categoryOptions,
  categoryPatch,
  type ItemType,
  typeLabel,
  typeOf,
  typeOptions,
  typePatch,
} from "../../../shared/part-type.ts";
import {
  filesOf,
  inProject,
  isBuilt,
  isPackagingPart,
  marginFrom,
  marginOf,
  PACK_TYPE_PLURALS,
  PACK_TYPES,
  PART_TYPE_LABELS,
  type PackType,
  type PartType,
  packagingOf,
  partProjects,
  printGaps,
  projectOfItem,
  projectPath,
} from "../../../shared/projects.ts"; // Manufacturing projects ---
import type { SpecFileKind } from "../../../shared/specs.ts";
import { hhlStock, stockNote, untrackedIds } from "../../../shared/stock.ts";
import { editVariants, type ProductPatch, type VariantPatch } from "../lib/catalog.ts";
import { useLandedCosts } from "../lib/costs.ts"; // Production runs ---
import { days, LIFECYCLE_LABELS, money, options, PROCUREMENT_LABELS, plural, qty, signed } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { imageFrom, uploadPhoto } from "../lib/photos.ts";
import { useProjectIndex, useUsedIn } from "../lib/projects.ts"; // Manufacturing projects ---
import { Link, setSearch, useSearch } from "../lib/router.tsx";
import { useRow, useRows } from "../lib/sync.ts";
import { CreateDialog } from "../ui/CreateDialog.tsx";
import { Badge, Button, cx, Select, Thumb } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { DialogLayer } from "../ui/DialogLayer.tsx"; // Vendors --- a link's full form, beside its row
import { isTyping, ListPage } from "../ui/ListPage.tsx";
import { type FieldDef, Fields, RecordPanel, Section } from "../ui/RecordPanel.tsx";
import { PaneLink, usePaneOpen } from "../ui/SidePane.tsx";
import { NotTracked, stockColumns, useStockIndex } from "../ui/StockColumns.tsx"; // Stock everywhere ---
import { StockNumber } from "../ui/StockEditor.tsx"; // Stock changes ---
import { toast } from "../ui/toast.tsx";
import { BoardSection } from "./BoardSection.tsx"; // Board components ---
import { BomItemSection } from "./BomItemSection.tsx";
import { BulkBar, parseMoney, STATUS_OPTIONS, SyncStatus, saveProduct, saveVariant, useShopifyWrites, WriteMarker, worst } from "./CatalogEditor.tsx";
import { ForecastSection } from "./ForecastSection.tsx"; // Forecasting ---
import { ItemPlanSection } from "./ItemPlanSection.tsx";
import { SalesSection, useSoldColumn } from "./ItemSales.tsx";
import { LocalStock } from "./ItemStock.tsx"; // Receiving ---
import { ItemSummary } from "./ItemSummary.tsx";
import { NeedChips, NewPackagingDialog, PackagingSection, packCategoryPatch } from "./PackagingSection.tsx"; // Packaging ---
import { GroupSection, MakeGroupDialog, memberFile, usePartGroups } from "./PartGroups.tsx"; // Part groups ---
import { NewPoDialog } from "./po/NewPo.tsx"; // Add to a draft PO --- the one New PO dialog
import { OnOrder } from "./po/OnOrder.tsx";
import { PartProjectChips, ProjectChip, partProjectsText } from "./projects/ProjectChips.tsx"; // Manufacturing projects ---
import { PriceHistorySection } from "./projects/runs/PriceHistory.tsx"; // Production runs ---
import { ItemReceipts } from "./receiving/ItemReceipts.tsx";
import { useSpecIndex } from "./specs/lib.ts";
import { SpecSection } from "./specs/SpecSection.tsx"; // Specs ---
import { VendorSide } from "./VendorPortal.tsx"; // Vendor portal ---

// Manufacturing projects --- "part": the Parts library (every local item: materials, sub-assemblies, services).
type Kind = "material" | "subassembly" | "sell" | "part";

export const itemPath = (item: Pick<Item, "kind" | "id">) => `${item.kind === "sell" ? "/items/sell" : "/parts"}?open=${item.id}`;

const shopifyAdminUrl = (item: Item) => {
  const product = item.shopify_product_id?.split("/").pop();
  const variant = item.shopify_variant_id?.split("/").pop();
  return product ? `https://admin.shopify.com/store/hand-held-legend/products/${product}${variant ? `/variants/${variant}` : ""}` : null;
};

/** "$4.99" or "$4.99–$9.99" across a product's variants. */
const range = (values: (number | null)[]) => {
  const v = values.filter((x): x is number => x != null);
  if (!v.length) return "";
  const [lo, hi] = [Math.min(...v), Math.max(...v)];
  return lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`;
};

// Gross margin on Shopify's price and cost per item: marginOf() in shared/projects.ts (project costs use the same rule).
const marginText = (m: number) => `${Math.round(m * 100)}%`;
const marginTone = (m: number) => (m < 0 ? "text-danger" : m < 0.3 ? "text-warn" : "");

/** Packaging --- a packaging part's kind for the ?pack= filter ("unset" until it's said). */
const packOf = (i: Item) => packagingOf(i)?.type ?? "unset";

const productId = (i: Item) => i.shopify_product_id ?? "";
/** A product with one variant shows as one row, which stands for the product too. */
const singleVariant = (i: Item) => !i.shopify_variant_title;
const splitTags = (text: string) =>
  text
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

function saveTitle(id: string | null, text: string) {
  if (!text.trim()) return toast.error("A product needs a title");
  saveProduct(id, { title: text.trim() });
}

type MoneyField = "price" | "compare_at_price" | "std_cost";
/** Spreadsheet editing for a money column: one variant's value, or every variant at once from the product's row. */
function moneyEditors(field: MoneyField, label: string): Pick<Column<Item>, "edit" | "groupEdit"> {
  const parse = (text: string) => parseMoney(text, label, { required: field === "price" });
  return {
    edit: {
      get: (i) => (i[field] == null ? "" : String(i[field])),
      save: (i, text) => {
        const v = parse(text);
        if (v !== undefined) saveVariant(i, { [field]: v } as VariantPatch);
      },
    },
    groupEdit: {
      get: (rs) => (new Set(rs.map((r) => r[field])).size === 1 && rs[0][field] != null ? String(rs[0][field]) : ""),
      save: (rs, text) => {
        const v = parse(text);
        if (v !== undefined) editVariants(rs.map((r) => ({ item_id: r.id, patch: { [field]: v } as VariantPatch })));
      },
    },
  };
}

/** Lookups every items view needs, rebuilt only when the underlying rows change. */
function useItemIndex() {
  const vendors = useRows("vendor");
  const links = useRows("vendor_item");
  const stock = useRows("stock_level");
  const ledger = useRows("stock_balance"); // Receiving --- local items' stock is HHL Ops' own ledger
  const projects = useRows("project");
  const locations = useRows("location");
  const items = useRows("item");
  return useMemo(() => {
    const vendorById = new Map(vendors.map((v) => [v.id, v]));
    const projectById = new Map(projects.map((p) => [p.id, p]));
    const preferred = new Map<string, VendorItem>();
    for (const l of links) if (l.is_preferred || !preferred.has(l.item_id)) preferred.set(l.item_id, l);
    // HHL's own locations (shared/stock.ts): available is what's free to sell (on hand less open orders). Consignment ---
    // stock at CM sites shows beside it (atCms), never in it.
    const atHhl = hhlStock(stock, ledger, locations, untrackedIds(items)); // a labor SKU Shopify doesn't track: no stock
    const cms = new Map(locations.filter((l) => l.is_cm && l.active && !l.ignored).map((l) => [l.id, l.name]));
    const atCms = new Map<string, { qty: number; names: Set<string> }>();
    for (const r of [...stock.map((s) => ({ item_id: s.item_id, location_id: s.location_id, qty: s.on_hand })), ...ledger]) {
      const cm = cms.get(r.location_id);
      if (cm && Number(r.qty) !== 0) {
        const at = atCms.get(r.item_id) ?? { qty: 0, names: new Set<string>() };
        at.qty += Number(r.qty);
        at.names.add(cm);
        atCms.set(r.item_id, at);
      }
    }
    return { vendorById, projectById, preferred, atHhl, atCms };
  }, [vendors, links, stock, ledger, projects, locations, items]);
}

export function ItemsPage({ kind, title }: { kind: Kind; title: string }) {
  const all = useRows("item");
  const index = useItemIndex();
  const stockIx = useStockIndex(); // Stock everywhere ---
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [grouped, setGrouped] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [ordering, setOrdering] = useState(false); // Add to a draft PO --- the New PO dialog for what's ticked
  const projects = useRows("project");
  const products = useRows("shopify_product");
  const writes = useShopifyWrites();
  const sold = useSoldColumn();
  // Production runs --- the margin's cost: the landed cost when the item has one, else Shopify's.
  const landed = useLandedCosts();
  const canEdit = kind === "sell" && can("shopify.edit_variants");
  // Part groups --- a group's parts fold under one row (on by default); ?group= shows one group; picking parts makes one
  const pg = usePartGroups();
  const specs = useSpecIndex();
  const [making, setMaking] = useState<Item[] | null>(null);
  // Manufacturing projects --- filters (?kind= for parts, ?built= for sell items, ?project= for both), and where parts go
  const px = useProjectIndex();
  const usedIn = useUsedIn();
  const params = useSearch();
  const kindFilter = kind === "part" ? (params.get("kind") ?? "") : "";
  const hasServices = useMemo(() => all.some((i) => i.kind === "service" && i.source === "local" && !i.archived), [all]); // Charges ---
  const builtFilter = kind === "sell" ? (params.get("built") ?? "") : "";
  const projectFilter = params.get("project") ?? "";
  // A new part has no project: from ?project= its SKU starts with the project's code after its type's prefix (MAT-GCU2-:
  // shared/part-sku.ts), and it joins the project once one of its BOMs uses it (until then it isn't in this list, so the
  // toast says why).
  const filtered = projectFilter && projectFilter !== "none" ? px.projectById.get(projectFilter) : undefined;
  const skuStart = filtered?.code ? `${filtered.code}-` : "";
  const notOnBomYet = `Not on a BOM yet: it joins ${filtered?.name ?? "a project"} when one of its BOMs uses it`;
  // A sub-assembly nothing uses (a kit) is in its own BOM's Project: Start its BOM starts that as the filtered project.
  const newKit = filtered
    ? `Nothing uses it yet: Start its BOM to put it in ${filtered.name}, or add it to one of ${filtered.name}'s BOMs`
    : "Nothing uses it yet: set Project on its BOM, or add it to another BOM";
  const groupFilter = kind === "part" ? (params.get("group") ?? "") : ""; // Part groups ---
  // Packaging --- the Packaging view (Type Packaging: part_type packaging, whatever its kind) and its Category (?pack=, "unset")
  const packaging = kindFilter === "packaging";
  const packFilter = packaging ? (params.get("pack") ?? "") : "";

  const base = useMemo(
    () =>
      all.filter(
        (i) =>
          (kind === "sell" ? i.source === "shopify" : kind === "part" ? i.source === "local" : i.kind === kind) &&
          (showArchived || !i.archived) &&
          (!kindFilter ||
            (kindFilter === "packaging"
              ? isPackagingPart(i)
              : kindFilter === "material"
                ? i.kind === "material" && !isPackagingPart(i) // Packaging --- packaging has its own view
                : i.kind === kindFilter)) &&
          (!builtFilter || (builtFilter === "built") === isBuilt(px, i)) &&
          (!projectFilter || inProject(px, i, projectFilter)) &&
          (!groupFilter || i.part_group_id === groupFilter),
      ),
    [all, kind, showArchived, kindFilter, builtFilter, projectFilter, groupFilter, px],
  );
  // Packaging --- how many of each Category the view has, for the Category filter
  const packCounts = useMemo(() => {
    const n = new Map<string, number>();
    if (packaging) for (const i of base) n.set(packOf(i), (n.get(packOf(i)) ?? 0) + 1);
    return n;
  }, [base, packaging]);
  const rows = useMemo(() => (packFilter ? base.filter((i) => packOf(i) === packFilter) : base), [base, packFilter]);

  const columns = useMemo<Column<Item>[]>(() => {
    const marginWith = (i: Item) => {
      const l = landed.get(i.id);
      return l
        ? { margin: marginFrom(i.price, l.cost), cost: l.cost as number | null, source: l.text }
        : { margin: marginOf(i), cost: i.std_cost, source: "Shopify's cost (no landed cost yet)" };
    };
    const vendorOf = (i: Item) => {
      const link = index.preferred.get(i.id);
      const v = link && index.vendorById.get(link.vendor_id);
      return v ? v.short_name || v.name : "";
    };
    const common: Column<Item>[] = [
      { key: "photo", header: "", width: "40px", value: () => "", cell: (i) => <Thumb src={i.photo_url} /> },
      { key: "sku", header: "SKU", width: "150px", cell: (i) => <span className="font-mono text-[12px]">{i.sku}</span> },
      {
        key: "name",
        header: "Name",
        width: "minmax(240px,2fr)",
        cell: (i, { nested }) => {
          // Part groups --- under its group's row a part shows its values first; anywhere else its group, as a chip
          const g = i.part_group_id ? pg.byId.get(i.part_group_id) : undefined;
          const name = <span className={cx("truncate", i.archived && "text-faint line-through", nested && "text-muted")}>{i.name}</span>;
          if (!g) return name;
          return nested ? (
            <span className="flex min-w-0 items-center gap-2" title={i.name}>
              <span className={cx("shrink-0", !isCurrent(i) && "text-faint line-through")}>{memberLabel(g, i)}</span>
              {name}
            </span>
          ) : (
            <span className="flex min-w-0 items-center gap-1.5">
              {name}
              <button
                type="button"
                title={`In ${g.name}: show its parts`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearch({ group: g.id, open: null });
                }}
                className="inline-flex h-5 shrink-0 items-center gap-1 rounded bg-surface-2 px-1.5 text-[11px] text-muted hover:text-text"
              >
                <Boxes className="size-3" />
                {g.name}
              </button>
            </span>
          );
        },
      },
    ];
    // Manufacturing projects --- a chip that opens the project (a Shopify item's comes through its linked product)
    const projectChip = (id: string | null | undefined) => {
      const p = id ? index.projectById.get(id) : undefined;
      return p ? (
        <Link
          to={projectPath(p)}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-5 items-center rounded bg-accent-soft px-1.5 text-[11px] font-medium text-accent-text hover:underline"
        >
          {p.code || p.name}
        </Link>
      ) : null;
    };
    const project: Column<Item> = {
      key: "project",
      header: "Project",
      width: "120px",
      value: (i) => index.projectById.get(projectOfItem(px, i) ?? "")?.name ?? "",
      cell: (i) => projectChip(projectOfItem(px, i)),
    };
    const tags: Column<Item> = {
      key: "tags",
      header: "Tags",
      width: "minmax(120px,1fr)",
      value: (i) => i.tags.join(" "),
      cell: (i) => (
        <span className="flex gap-1">
          {i.tags.map((t) => (
            <Badge key={t} tone="accent">
              {t}
            </Badge>
          ))}
        </span>
      ),
    };
    // Available at HHL, not on hand: units on open orders are on the shelf but already sold. The tooltip says why they differ.
    const onHand: Column<Item> = {
      key: "available",
      header: "Available",
      headerTitle: "At HHL: on hand less units committed to customer orders (not shipped yet). Hover a number for the split.",
      width: "120px",
      align: "right",
      value: (i) => (i.shopify_tracked === false ? null : (index.atHhl.get(i.id)?.available ?? null)),
      text: (i) => (i.shopify_tracked === false ? "Not tracked" : (index.atHhl.get(i.id)?.available ?? 0)),
      // Consignment --- HHL's; what HHL has at CMs follows it ("+40 at CM")
      cell: (i) => {
        if (i.shopify_tracked === false) return <NotTracked />;
        const cm = index.atCms.get(i.id);
        const s = index.atHhl.get(i.id);
        const note = stockNote(s);
        return (
          <>
            <StockNumber
              itemId={i.id}
              className={cx(note && "underline decoration-dotted underline-offset-2", (s?.available ?? 0) < 0 && "text-danger")}
              title={note ?? undefined}
            >
              {qty(s?.available)}
            </StockNumber>
            {cm && cm.qty !== 0 && (
              <span
                className={cx("ml-1 text-[11px]", cm.qty < 0 ? "text-danger" : "text-faint")}
                title={`${qty(cm.qty)} at ${[...cm.names].join(", ")}${cm.qty < 0 ? ": below zero, a count or shipment is missing" : ""}`}
              >
                {signed(cm.qty)} at CM
              </span>
            )}
          </>
        );
      },
    };
    if (kind === "sell") {
      const productById = new Map(products.map((p) => [p.id, p]));
      const shopifyTags = (i: Item) => productById.get(productId(i))?.tags ?? [];
      const status = (s: string) => <Badge tone={s === "ACTIVE" ? "ok" : "neutral"}>{s.toLowerCase() || "—"}</Badge>;
      const vendors = (rs: Item[]) => [...new Set(rs.map((i) => vendorOf(i) || i.shopify_vendor).filter(Boolean))];
      const named = (text: string, state: ReturnType<typeof worst>, archived = false) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={cx("truncate", archived && "text-faint line-through")}>{text}</span>
          <WriteMarker state={state} />
        </span>
      );
      const tagBadges = (list: string[]) => (
        <span className="flex gap-1 font-normal">
          {list.map((t) => (
            <Badge key={t}>{t}</Badge>
          ))}
        </span>
      );
      const saveStatus = (id: string | null, value: string) => saveProduct(id, { status: value as ProductPatch["status"] });
      const mono = (field: "sku" | "barcode", header: string, width: string): Column<Item> => ({
        key: field,
        header,
        width,
        cell: (i) => <span className="font-mono text-[12px]">{i[field]}</span>,
        edit: { get: (i) => i[field], save: (i, text) => saveVariant(i, { [field]: text.trim() }) },
      });
      const sell: (Column<Item> | false)[] = [
        {
          key: "photo",
          header: "",
          width: grouped ? "72px" : "40px",
          value: () => "",
          cell: (i) => <Thumb src={i.photo_url} />,
          groupCell: (rs) => <Thumb src={rs.find((r) => r.photo_url)?.photo_url} />,
        },
        {
          ...mono("sku", "SKU", "150px"),
          groupCell: (rs) => <span className="text-[12px] font-normal text-faint">{rs.length} variants</span>,
        },
        {
          key: "name",
          header: "Name",
          width: "minmax(240px,2fr)",
          cell: (i, { nested }) => named(nested ? i.shopify_variant_title : i.name, worst(writes.item.get(i.id), writes.product.get(productId(i))), i.archived),
          groupCell: (rs) => named(rs[0].shopify_product_title, worst(writes.product.get(productId(rs[0])), ...rs.map((r) => writes.item.get(r.id)))),
          edit: { can: singleVariant, get: (i) => i.shopify_product_title, save: (i, text) => saveTitle(i.shopify_product_id, text) },
          groupEdit: { get: (rs) => rs[0].shopify_product_title, save: (rs, text) => saveTitle(rs[0].shopify_product_id, text) },
        },
        {
          key: "shopify_status",
          header: "Status",
          width: "100px",
          cell: (i) => status(i.shopify_status),
          groupCell: (rs) => status(rs[0].shopify_status),
          // Under its product, a variant's status is edited on the product's row.
          edit: {
            options: STATUS_OPTIONS,
            can: (i) => !grouped || singleVariant(i),
            get: (i) => i.shopify_status,
            save: (i, v) => saveStatus(i.shopify_product_id, v),
          },
          groupEdit: { options: STATUS_OPTIONS, get: (rs) => rs[0].shopify_status, save: (rs, v) => saveStatus(rs[0].shopify_product_id, v) },
        },
        {
          key: "price",
          header: "Price",
          width: "120px",
          align: "right",
          cell: (i) => money(i.price),
          groupCell: (rs) => range(rs.map((r) => r.price)),
          ...moneyEditors("price", "Price"),
        },
        editing && {
          key: "compare_at_price",
          header: "Compare at",
          width: "120px",
          align: "right",
          cell: (i) => money(i.compare_at_price),
          groupCell: (rs) => range(rs.map((r) => r.compare_at_price)),
          ...moneyEditors("compare_at_price", "Compare-at price"),
        },
        {
          key: "std_cost",
          header: "Cost",
          width: "120px",
          align: "right",
          cell: (i) => money(i.std_cost),
          groupCell: (rs) => range(rs.map((r) => r.std_cost)),
          ...moneyEditors("std_cost", "Cost"),
        },
        {
          key: "margin",
          header: "Margin",
          // Production runs --- the landed cost when the item has one (actual from its latest closed run, else planned), else Shopify's cost.
          headerTitle:
            "Price against the landed cost when the item has one (actual from its latest closed production run, else its planned cost), else against Shopify's cost. Hover a margin to see which.",
          width: "84px",
          align: "right",
          value: (i) => marginWith(i).margin,
          cell: (i) => {
            const { margin: m, cost, source } = marginWith(i);
            if (m === null)
              return (
                <span className="text-faint" title={cost == null ? "No landed cost and no cost in Shopify" : "No price"}>
                  —
                </span>
              );
            return (
              <span className={marginTone(m)} title={`${money(Number(i.price) - Number(cost))} a unit: ${source}`}>
                {marginText(m)}
                {source.startsWith("Shopify") ? "" : <span className="ml-0.5 text-[10px] text-faint">{source.startsWith("Actual") ? "A" : "P"}</span>}
              </span>
            );
          },
          groupCell: (rs) => {
            const ms = rs.map((r) => marginWith(r).margin).filter((m): m is number => m !== null);
            if (!ms.length) return "";
            const [lo, hi] = [Math.min(...ms), Math.max(...ms)];
            return (
              <span className={marginTone(lo)}>{Math.round(lo * 100) === Math.round(hi * 100) ? marginText(lo) : `${marginText(lo)}–${marginText(hi)}`}</span>
            );
          },
        },
        editing && mono("barcode", "Barcode", "140px"),
        { ...onHand, groupCell: (rs) => qty(rs.reduce((n, r) => n + (index.atHhl.get(r.id)?.available ?? 0), 0)) },
        ...stockColumns<Item>(stockIx, (i) => i.id, { skip: ["available"] }), // Stock everywhere ---
        !editing && sold,
        {
          key: "vendor",
          header: "Vendor",
          width: "150px",
          value: (i) => vendorOf(i) || i.shopify_vendor,
          cell: (i) => vendorOf(i) || <span className="text-faint">{i.shopify_vendor}</span>,
          groupCell: (rs) => {
            const v = vendors(rs);
            return v.length > 1 ? <span className="font-normal text-muted">{v.length} vendors</span> : <span className="font-normal">{v[0] ?? ""}</span>;
          },
        },
        // Editing swaps HHL Ops' own project and tags for the product's Shopify tags.
        editing
          ? {
              key: "shopify_tags",
              header: "Shopify tags",
              width: "minmax(180px,1fr)",
              value: (i) => shopifyTags(i).join(", "),
              cell: (i, { nested }) => (nested ? null : tagBadges(shopifyTags(i))),
              groupCell: (rs) => tagBadges(shopifyTags(rs[0])),
              edit: {
                can: singleVariant,
                get: (i) => shopifyTags(i).join(", "),
                save: (i, text) => saveProduct(i.shopify_product_id, { tags: splitTags(text) }),
              },
              groupEdit: { get: (rs) => shopifyTags(rs[0]).join(", "), save: (rs, text) => saveProduct(rs[0].shopify_product_id, { tags: splitTags(text) }) },
            }
          : {
              ...project,
              groupCell: (rs) => {
                const ids = new Set(rs.map((r) => projectOfItem(px, r)));
                const only = ids.size === 1 ? [...ids][0] : null;
                return <span className="font-normal">{projectChip(only)}</span>;
              },
            },
        !editing && tags,
      ];
      return sell.filter((c): c is Column<Item> => !!c);
    }
    const link = (i: Item) => index.preferred.get(i.id);
    // Packaging --- the Packaging view: what each part is ("Printed box") and where its print files stand, by need
    const packColumns: Column<Item>[] = [
      {
        key: "packaging",
        header: "Packaging",
        width: "170px",
        value: (i) => (packagingOf(i)?.type ? packagingOf(i)?.title : ""),
        cell: (i) => {
          const p = packagingOf(i);
          return p?.type ? (
            <span className="truncate" title={p.says}>
              {p.title}
            </span>
          ) : (
            <span className="text-[12px] font-medium text-warn" title={p?.says}>
              Category not set
            </span>
          );
        },
      },
      {
        key: "print_files",
        header: "Print files",
        headerTitle: "✓ in the released revision (what vendors get with each PO); “not released”: only uploaded to the draft spec",
        width: "minmax(220px,1.3fr)",
        // Sorted by what a vendor would still be missing (complete first).
        value: (i) => printGaps(i, filesOf(px, i).released)?.join(", ") ?? "",
        cell: (i) =>
          packagingOf(i)?.type ? <NeedChips item={i} f={filesOf(px, i)} className="min-w-0 overflow-hidden" /> : <span className="text-faint">—</span>,
      },
    ];
    // Manufacturing projects --- the Parts library: Type, Category, where it's used. A part has no project of its own:
    // it's in the projects whose BOMs use it (solid chips; dashed "· draft" when only a newer draft does), then the
    // products it ends up in; a kit nothing uses is in its BOM's project; else "Not on a BOM yet".
    const projectsOf = partProjects(px);
    // A part's Category as the table shows it: nothing for a material's or sub-assembly's Other, "Not set" for packaging
    // that hasn't said yet.
    const categoryText = (i: Item) => {
      const t = typeOf(i);
      if (t === "packaging") return categoryLabel(i) || "Not set";
      return categoryOf(i) === "other" ? "" : categoryLabel(i);
    };
    const usedInText = (i: Item) => {
      const u = usedIn.get(i.id);
      const pp = projectsOf.get(i.id);
      const projects = partProjectsText(px, i.id);
      if (!projects && !u) return pp?.onBom ? "In no project" : "Not on a BOM yet";
      return [projects, u ? u.products.join(", ") : pp?.inUse.length ? "Nothing uses it" : ""].filter(Boolean).join(" · ");
    };
    const partColumns: Column<Item>[] =
      kind === "part"
        ? [
            ...(packaging
              ? packColumns
              : [
                  { key: "kind", header: "Type", width: "110px", value: (i: Item) => typeLabel(i) },
                  { key: "part_type", header: "Category", width: "140px", value: categoryText },
                ]),
            {
              key: "used_in",
              header: "Used in",
              width: "minmax(220px,1.4fr)",
              value: usedInText,
              cell: (i) => {
                const u = usedIn.get(i.id);
                const pp = projectsOf.get(i.id);
                if (!u && !pp?.inUse.length && !pp?.onDraft.length)
                  return <span className="text-faint">{pp?.onBom ? "In no project" : "Not on a BOM yet"}</span>;
                return (
                  <span className="flex min-w-0 items-center gap-1">
                    <PartProjectChips px={px} id={i.id} />
                    {u ? (
                      <span className="truncate text-muted" title={u.products.join(", ")}>
                        {u.products.join(", ")}
                      </span>
                    ) : (
                      !!pp?.inUse.length && <span className="truncate text-muted">Nothing uses it</span>
                    )}
                  </span>
                );
              },
            },
          ]
        : [];
    const parts: Column<Item>[] = [
      ...common,
      ...partColumns,
      { key: "vendor", header: "Vendor", width: "150px", value: vendorOf },
      { key: "unit_price", header: "Price", width: "90px", align: "right", value: (i) => link(i)?.unit_price ?? null, cell: (i) => money(link(i)?.unit_price) },
      { key: "moq", header: "MOQ", width: "80px", align: "right", value: (i) => link(i)?.moq ?? null, cell: (i) => (link(i)?.moq ? qty(link(i)?.moq) : "") },
      { key: "lead", header: "Lead", width: "80px", align: "right", value: (i) => link(i)?.lead_time_days ?? null, cell: (i) => days(link(i)?.lead_time_days) },
      onHand,
      ...stockColumns<Item>(stockIx, (i) => i.id, { skip: ["available"] }), // Stock everywhere ---
      ...(packaging ? [] : [tags]),
    ];
    if (kind !== "part") return parts;

    // Part groups --- a group's row: what its current parts add up to, for display only (each keeps its own stock, terms
    // and files). While picking parts, a group's name, Type and Category are edited on its row.
    const groupOf = (rs: Item[]) => pg.byId.get(rs[0].part_group_id ?? "");
    const current = (rs: Item[]) => (rs.some(isCurrent) ? rs.filter(isCurrent) : rs);
    const same = <V,>(rs: Item[], f: (i: Item) => V): V | undefined => {
      const vs = new Set(rs.map(f));
      return vs.size === 1 ? [...vs][0] : undefined;
    };
    const span = (rs: Item[], f: (i: Item) => number | string | null | undefined, show: (n: number) => string) => {
      const v = rs
        .map(f)
        .filter((x): x is number | string => x != null && x !== "")
        .map(Number);
      if (!v.length) return "";
      const [lo, hi] = [Math.min(...v), Math.max(...v)];
      return lo === hi ? show(lo) : `${show(lo)}–${show(hi)}`;
    };
    const mixed = <span className="font-normal text-faint">mixed</span>;
    const setAll = (rs: Item[], patch: Parameters<typeof mutate<"part_group.set_all">>[1]["patch"]) => {
      const g = groupOf(rs);
      if (g) mutate("part_group.set_all", { id: g.id, patch });
    };
    const groupCells: Record<string, Pick<Column<Item>, "groupCell" | "groupEdit">> = {
      photo: { groupCell: (rs) => <Thumb src={rs.find((r) => r.photo_url)?.photo_url} /> },
      sku: {
        groupCell: (rs) => <span className="font-mono text-[12px] font-normal text-muted">{skuStem(groupOf(rs) ?? { sku_prefix: "" }, rs) || rs[0].sku}…</span>,
      },
      name: {
        groupCell: (rs) => {
          const g = groupOf(rs);
          return (
            <span className="flex min-w-0 items-center gap-1.5">
              <Boxes className="size-3.5 shrink-0 text-faint" />
              <span className="shrink-0">{g?.name}</span>
              <span className="min-w-0 truncate text-[12px] font-normal text-muted">{g ? groupSummary(g, current(rs)) : ""}</span>
            </span>
          );
        },
        groupEdit: {
          get: (rs) => groupOf(rs)?.name ?? "",
          save: (rs, text) => {
            const g = groupOf(rs);
            if (!text.trim()) return toast.error("A group needs a name");
            if (g) mutate("part_group.update", { id: g.id, patch: { name: text.trim() } });
          },
        },
      },
      // A group's parts share one Type (and one Category, unless they're packaging): set on all of them at once.
      kind: {
        groupCell: (rs) => same(rs, typeLabel) ?? mixed,
        groupEdit: {
          options: (rs) => typeOptions(same(rs, typeOf) ?? ""),
          get: (rs) => same(rs, typeOf) ?? "",
          save: (rs, v) => setAll(rs, typePatch(rs[0], v as ItemType)),
        },
      },
      part_type: {
        groupCell: (rs) => same(rs, categoryText) ?? mixed,
        groupEdit: {
          // Its Type's choices (a group of services has none, so its cell isn't edited)
          options: (rs) => categoryOptions(typeOf(rs[0])),
          can: (rs) => categoryOptions(typeOf(rs[0])).length > 0 && same(rs, typeOf) != null,
          get: (rs) => same(rs, categoryOf) ?? "",
          save: (rs, v) => setAll(rs, typeOf(rs[0]) === "packaging" ? { pack_type: v as PackType } : { part_type: v as PartType }),
        },
      },
      packaging: {
        groupCell: (rs) => same(rs, (r) => (packagingOf(r)?.type ? packagingOf(r)?.title : "Category not set")) ?? mixed,
        groupEdit: {
          options: categoryOptions("packaging"),
          get: (rs) => same(rs, (r) => r.pack_type) ?? "",
          save: (rs, v) => setAll(rs, { pack_type: v as PackType }),
        },
      },
      print_files: {
        groupCell: (rs) => {
          const g = groupOf(rs);
          const cur = current(rs);
          const shared = (g?.shared_files ?? []).map((k) => {
            const files = cur.map((r) => memberFile(specs, r.id, k as SpecFileKind));
            const shas = new Set(files.map((f) => f.draft).filter(Boolean));
            const have = files.filter((f) => f.draft).length;
            const label = SHARED_FILE_LABELS[k as SpecFileKind];
            return have === cur.length && shas.size === 1
              ? `${label} same on ${cur.length}`
              : `${label} on ${have}/${cur.length}${shas.size > 1 ? " (differs)" : ""}`;
          });
          const art = cur.filter((r) => packagingOf(r)?.files.some((f) => f.kinds.includes("artwork")));
          const released = art.filter((r) => filesOf(px, r).released?.has("artwork")).length;
          const parts = [...shared, art.length ? `Artwork released ${released}/${art.length}` : ""].filter(Boolean);
          return <span className="truncate text-[12px] font-normal text-muted">{parts.join(" · ")}</span>;
        },
      },
      used_in: {
        // Manufacturing projects --- its parts' projects together (a project only a draft brings one of them into: dashed)
        groupCell: (rs) => {
          const name = (id: string) => px.projectById.get(id)?.name ?? id;
          const inUse = [...new Set(rs.flatMap((r) => projectsOf.get(r.id)?.inUse ?? []))].sort((a, b) => name(a).localeCompare(name(b)));
          const drafts = new Map<string, string>(); // project → a part only its draft uses
          for (const r of rs) for (const id of projectsOf.get(r.id)?.onDraft ?? []) if (!inUse.includes(id) && !drafts.has(id)) drafts.set(id, r.id);
          // Parts nothing in use uses, and of those the ones a newer draft does (dashed chips on their own rows)
          const idle = current(rs).filter((r) => !usedIn.get(r.id));
          const draftOnly = idle.filter((r) => projectsOf.get(r.id)?.onDraft.length).length;
          const unused = idle.length - draftOnly;
          const idleText = [unused > 0 && `${unused} used by nothing`, draftOnly > 0 && `${draftOnly} only on a draft`].filter(Boolean).join(" · ");
          return (
            <span className="flex min-w-0 items-center gap-1 font-normal">
              {inUse.map((id) => (
                <ProjectChip key={id} px={px} id={id} />
              ))}
              {[...drafts].map(([id, part]) => (
                <ProjectChip key={id} px={px} id={id} draftOf={part} />
              ))}
              {!inUse.length && !drafts.size ? (
                <span className="text-faint">{rs.some((r) => projectsOf.get(r.id)?.onBom) ? "In no project" : "Not on a BOM yet"}</span>
              ) : (
                !!idleText && <span className="truncate text-[12px] text-muted">{idleText}</span>
              )}
            </span>
          );
        },
      },
      vendor: {
        groupCell: (rs) => {
          const v = new Set(current(rs).map(vendorOf).filter(Boolean));
          return <span className="font-normal">{v.size > 1 ? `${v.size} vendors` : ([...v][0] ?? "")}</span>;
        },
      },
      unit_price: { groupCell: (rs) => <span className="font-normal">{range(current(rs).map((r) => link(r)?.unit_price ?? null))}</span> },
      moq: { groupCell: (rs) => <span className="font-normal">{span(current(rs), (r) => link(r)?.moq || null, qty)}</span> },
      lead: {
        groupCell: (rs) => (
          <span className="font-normal">
            {span(
              current(rs),
              (r) => link(r)?.lead_time_days,
              (n) => days(n),
            )}
          </span>
        ),
      },
      available: {
        // The sum, never pooled: the part with the least is named, since each one is bought and counted on its own.
        groupCell: (rs) => {
          const each = current(rs).map((r) => ({ r, n: index.atHhl.get(r.id)?.available ?? 0 }));
          const low = each.reduce((a, b) => (b.n < a.n ? b : a), each[0]);
          return (
            <span title={each.map((x) => `${x.r.sku}: ${qty(x.n)}`).join("\n")}>
              {qty(each.reduce((n, x) => n + x.n, 0))}
              {low && each.length > 1 && (
                <span className="ml-1 text-[11px] font-normal text-faint">
                  low {low.r.sku.slice(skuStem(groupOf(rs) ?? { sku_prefix: "" }, rs).length) || low.r.sku} {qty(low.n)}
                </span>
              )}
            </span>
          );
        },
      },
    };
    return parts.map((c) => (groupCells[c.key] ? { ...c, ...groupCells[c.key] } : c));
  }, [kind, index, stockIx, grouped, editing, products, writes, sold, usedIn, px, landed, packaging, pg, specs]);

  const selectedItems = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);

  return (
    <>
      <ListPage
        title={title}
        rows={rows}
        columns={columns}
        defaultSort={{ key: kind === "sell" && grouped ? "name" : "sku" }}
        grouping={
          kind === "sell" && grouped
            ? { by: (i) => (i.shopify_variant_title ? i.shopify_product_id : null), groupNoun: ["product", "products"], rowNoun: ["variant", "variants"] }
            : kind === "part" && grouped // Part groups --- a group's parts under one row
              ? { by: (i) => i.part_group_id, groupNoun: ["row", "rows"], rowNoun: ["part", "parts"] }
              : undefined
        }
        newLabel={packaging ? "New packaging part" : kind === "subassembly" ? "New sub-assembly" : kind === "part" ? "New part" : "New material"}
        onNew={kind !== "sell" && can("item.create") ? () => setCreating(true) : undefined}
        pane="item"
        editing={editing}
        // Add to a draft PO --- items tick outside editing too (sell items and parts), for "Add to PO…"
        selection={editing || ((kind === "sell" || kind === "part") && can("po.create")) ? { selected, onChange: setSelected } : undefined}
        banner={
          // Part groups --- picking parts to group: what's picked and Make a group…
          kind === "part" && editing ? (
            <div className="flex h-9 shrink-0 items-center gap-2 border-b border-line bg-accent-soft/40 px-4 text-[12px]">
              <Boxes className="size-3.5 shrink-0 text-accent-text" />
              <span className="min-w-0 truncate">
                {selectedItems.length
                  ? `${plural(selectedItems.length, "part")} picked`
                  : "Tick the parts that are one thing in different colours or models (a group's row ticks all of it). A group's name, Type and Category edit on its row."}
              </span>
              {selectedItems.length > 0 && (
                <>
                  <Button size="sm" variant="primary" icon={<Boxes className="size-3.5" />} onClick={() => setMaking(selectedItems)}>
                    Make a group of {selectedItems.length}…
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                </>
              )}
            </div>
          ) : editing && selectedItems.length > 0 ? (
            <BulkBar selected={selectedItems} onClear={() => setSelected(new Set())} />
          ) : !editing && selectedItems.length > 0 ? (
            // Add to a draft PO --- what's ticked onto its vendor's draft
            <div className="flex h-9 shrink-0 items-center gap-2 border-b border-line bg-accent-soft/40 px-4 text-[12px]">
              <span>{plural(selectedItems.length, kind === "sell" ? "item" : "part")} picked</span>
              <Button size="sm" variant="primary" icon={<FilePlus2 className="size-3.5" />} onClick={() => setOrdering(true)}>
                Add to PO…
              </Button>
              {ordering && <NewPoDialog items={selectedItems} onClose={() => setOrdering(false)} onDone={() => setSelected(new Set())} />}
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </div>
          ) : packaging ? (
            // Packaging --- by Category: the ones there are, with how many of each
            <div className="flex items-center gap-2 border-b border-line bg-surface px-4 py-1.5">
              <span className="text-[12px] text-muted">Category</span>
              <FilterChips
                wrap
                value={packFilter}
                onChange={(v) => setSearch({ pack: v || null })}
                options={[
                  ["", `All ${base.length}`],
                  ...PACK_TYPES.filter((t) => packCounts.get(t) || packFilter === t).map((t): [string, string] => [
                    t,
                    `${PACK_TYPE_PLURALS[t]} ${packCounts.get(t) ?? 0}`,
                  ]),
                  ...(packCounts.get("unset") || packFilter === "unset" ? [["unset", `Not set ${packCounts.get("unset") ?? 0}`] as [string, string]] : []),
                ]}
              />
            </div>
          ) : undefined
        }
        toolbar={
          <>
            {/* Manufacturing projects --- General / Built, kinds, project */}
            {kind === "sell" && (
              <FilterChips
                value={builtFilter}
                onChange={(v) => setSearch({ built: v || null })}
                options={[
                  ["", "All"],
                  ["general", "General"],
                  ["built", "Built"],
                ]}
                title="Built: has a BOM or belongs to a project"
              />
            )}
            {kind === "part" && (
              <FilterChips
                value={kindFilter}
                onChange={(v) => setSearch({ kind: v || null, pack: null })}
                options={[
                  ["", "All"],
                  ["material", "Materials"],
                  ["packaging", "Packaging"],
                  ["subassembly", "Sub-assemblies"],
                  // Charges --- only while there are service parts (what a maker charges is a charge on its BOM now)
                  ...(hasServices || kindFilter === "service" ? ([["service", "Services"]] as [string, string][]) : []),
                ]}
              />
            )}
            {kind !== "sell" || builtFilter !== "general" ? (
              <div className="w-40">
                <Select
                  value={projectFilter}
                  onChange={(e) => setSearch({ project: e.target.value || null })}
                  options={[
                    { value: "", label: "Every project" },
                    ...projects.map((p) => ({ value: p.id, label: p.name })),
                    { value: "none", label: "In no project" },
                  ]}
                />
              </div>
            ) : null}
            {/* Part groups --- one group's parts (from its chip, or Ctrl+K) */}
            {groupFilter && (
              <span className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-accent-soft px-2 text-[12px] text-accent-text">
                <Boxes className="size-3.5" />
                {pg.byId.get(groupFilter)?.name ?? "A group"}
                <button type="button" title="Show every part" className="rounded hover:bg-hover" onClick={() => setSearch({ group: null })}>
                  <X className="size-3.5" />
                </button>
              </span>
            )}
            {kind === "sell" && <SyncStatus />}
            {/* The buttons stay together when a stacked header wraps its toolbar, wrapping themselves only in a column too narrow for them */}
            <div className="flex items-center gap-2 [[data-stacked]_&]:flex-wrap">
              {canEdit && (
                <Button
                  size="sm"
                  variant={editing ? "primary" : "ghost"}
                  icon={editing ? <Check className="size-3.5" /> : <Pencil className="size-3.5" />}
                  title={editing ? undefined : "Edit prices, SKUs, titles, status and tags here; changes go to Shopify"}
                  onClick={() => {
                    setEditing((e) => !e);
                    setSelected(new Set());
                  }}
                >
                  {editing ? "Done editing" : "Edit"}
                </Button>
              )}
              {kind === "part" && can("part_group.create") && (
                <Button
                  size="sm"
                  variant={editing ? "primary" : "ghost"}
                  icon={editing ? <Check className="size-3.5" /> : <Boxes className="size-3.5" />}
                  title={editing ? undefined : "Pick parts that are one thing in different colours or models, and group them"}
                  onClick={() => {
                    setEditing((e) => !e);
                    setSelected(new Set());
                  }}
                >
                  {editing ? "Done" : "Make a group…"}
                </Button>
              )}
              {kind === "sell" && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={grouped ? <List className="size-3.5" /> : <ListTree className="size-3.5" />}
                  onClick={() => setGrouped((g) => !g)}
                >
                  {grouped ? "Flat list" : "Group by product"}
                </Button>
              )}
              {/* Part groups --- folded under one row or every part on its own (an icon: the header has the panel beside it) */}
              {kind === "part" && pg.groups.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={grouped ? <List className="size-3.5" /> : <ListTree className="size-3.5" />}
                  title={grouped ? "Flat list: every part on its own row" : "Group by part group: a group's parts under one row"}
                  aria-label={grouped ? "Flat list" : "Group by part group"}
                  onClick={() => setGrouped((g) => !g)}
                />
              )}
              {/* The Parts library's header is full (kinds, project, groups, New packaging part): an icon there, as above */}
              <Button
                size="sm"
                variant={showArchived ? "secondary" : "ghost"}
                icon={<Archive className="size-3.5" />}
                title={kind === "part" ? (showArchived ? "Hiding nothing: archived parts show too" : "Show archived parts") : undefined}
                aria-label={kind === "part" ? (showArchived ? "Hiding nothing" : "Show archived") : undefined}
                onClick={() => setShowArchived((s) => !s)}
              >
                {kind === "part" ? null : showArchived ? "Hiding nothing" : "Show archived"}
              </Button>
            </div>
          </>
        }
        empty={kind === "sell" ? <div className="p-6 text-center text-muted">No Shopify items yet. Connect Shopify under Settings → Shopify.</div> : undefined}
      />
      {/* Part groups --- the picked parts' group: its name, what they differ by, each one's values */}
      {making && (
        <MakeGroupDialog
          items={making}
          onClose={() => setMaking(null)}
          onMade={() => {
            setSelected(new Set());
            setEditing(false);
            setSearch({ open: making[0].id });
            setMaking(null);
          }}
        />
      )}
      {/* Packaging --- asks its Category and printing (prefilled from ?pack=) */}
      {creating && packaging && (
        <NewPackagingDialog
          initial={{ type: packFilter, sku: skuStart }}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setSearch({ open: id });
            toast.info(notOnBomYet);
          }}
        />
      )}
      {creating && kind !== "sell" && !packaging && (
        <CreateDialog
          title={kind === "subassembly" ? "New sub-assembly" : kind === "part" ? "New part" : "New material"}
          initial={kind === "part" ? { type: kindFilter || "material", category: "other", sku: skuStart } : { sku: skuStart }}
          fields={[
            ...(kind === "part"
              ? [
                  { key: "type", label: "Type", type: "select" as const, options: typeOptions() },
                  // Manufacturing projects --- its choices follow the Type (a service has none); it decides the files its spec should have
                  {
                    key: "category",
                    label: "Category",
                    type: "select" as const,
                    optionsFor: (v: Record<string, string>) => categoryOptions(v.type as ItemType),
                  },
                ]
              : []),
            { key: "name", label: "Name", placeholder: "e.g. GCU2 RM2 Radio Module" },
            // Part SKUs --- its type's prefix is fixed in front and follows the Type (MAT- → PAK- for packaging)
            {
              key: "sku",
              label: "SKU",
              placeholder: "e.g. GCU2-RM2",
              prefix: (v) => skuPrefix(kind === "part" ? typePatch({ kind: "material" }, v.type as ItemType) : { kind }),
            },
          ]}
          onClose={() => setCreating(false)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            // A part's Type and Category as they're stored: kind + part_type, and packaging's Category in pack_type
            const typed = kind === "part" ? typePatch({ kind: "material" }, v.type as ItemType) : { kind };
            const category = kind === "part" && categoryOptions(v.type as ItemType).some((o) => o.value === v.category) ? categoryPatch(typed, v.category) : {};
            const as = typed.kind;
            const ok = await mutate("item.create", {
              id,
              name: v.name,
              sku: v.sku,
              procurement: as === "subassembly" ? "make" : "buy",
              ...typed,
              ...category,
            });
            if (ok) {
              setSearch({ open: id });
              toast.info(as === "subassembly" ? newKit : notOnBomYet);
            }
            return ok;
          }}
        />
      )}
    </>
  );
}

/** Manufacturing projects --- a row of mutually exclusive filter buttons (one of them on). */
function FilterChips({
  value,
  onChange,
  options,
  title,
  wrap,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly (readonly [string, string])[];
  title?: string;
  /** Outside the header (the packaging kind row): wrap whenever it's narrower than its chips. In the header it wraps only
   * while stacked (ListPage), on a row of its own, never inside the one-row header. */
  wrap?: boolean;
}) {
  return (
    <div className={cx("flex rounded-md border border-line p-0.5", wrap ? "flex-wrap" : "[[data-stacked]_&]:flex-wrap")} title={title}>
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cx(
            "h-6 rounded px-2 text-[12px] whitespace-nowrap",
            value === v ? "bg-selected font-medium text-text" : "text-muted hover:bg-hover hover:text-text",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// Manufacturing projects --- exported so the project workspace opens the same panel in place.
export function ItemPanel({ id, close }: { id: string; close: () => void }) {
  const item = useRow("item", id);
  const projects = useRows("project");
  const allItems = useRows("item");
  const tagSuggestions = useMemo(() => [...new Set(allItems.flatMap((i) => i.tags))].sort(), [allItems]);
  const writes = useShopifyWrites();
  const px = useProjectIndex();
  const [makeSectionsFor, setMakeSectionsFor] = useState<string | null>(null); // "Add one": this item only
  const specs = useSpecIndex();
  const groups = usePartGroups(); // Part groups ---
  if (!item) return null;
  // Part groups --- a group's parts share one Type, and one Category unless they're packaging (pack_type isn't locked):
  // changed for all of them in its Group section
  const sharedType = !!item.part_group_id && (groups.membersByGroup.get(item.part_group_id)?.length ?? 0) > 1;

  const shopify = item.source === "shopify";
  // A sell item bought finished (no project, no BOM, procurement Buy) has no BOM or spec to show: no "No BOM yet".
  const boughtFinished = item.kind === "sell" && item.procurement === "buy" && !isBuilt(px, item) && makeSectionsFor !== item.id;
  // Packaging --- its Category and printing decide its print files; plain and labelled ones need no spec ("No spec yet" isn't shown)
  const pack = packagingOf(item);
  // Manufacturing projects --- a sell item has its project; a part has none of its own (ItemSummary shows its projects)
  const projectOptions = [{ value: "", label: "No project" }, ...projects.map((p) => ({ value: p.id, label: p.name }))];
  const project: FieldDef = { key: "project_id", label: "Project", type: "select", options: projectOptions, nullable: true };
  // A part's Type and Category (virtual fields: `row` carries `type` and `category`); a Type change writes kind and
  // part_type (the server swaps the SKU's prefix), a Category change part_type, or a packaging part's pack_type (with
  // printing its new Category can't have back to the usual, as the Packaging section did).
  const type = typeOf(item);
  const typeField: FieldDef = {
    key: "type",
    label: "Type",
    type: "select",
    // (a local item made as a sell item, like the demo's scale-test ones, shows as one until it's given a Type)
    options: [...(type === "sell" ? [{ value: "sell", label: "Sell item" }] : []), ...typeOptions(type)],
    readOnly: sharedType,
    save: (v) => v !== type && mutate("item.update", { id: item.id, patch: typePatch(item, v as ItemType) }),
  };
  // A sell item used as a part (a Shopify joystick) has a material's Category: it decides the files its spec should have.
  const categoryChoices =
    type === "sell"
      ? [
          ...categoryOptions("material"),
          ...(categoryOptions("material").some((o) => o.value === item.part_type) ? [] : [{ value: item.part_type, label: PART_TYPE_LABELS[item.part_type] }]),
        ]
      : type === "packaging" && !item.pack_type
        ? [{ value: "", label: "Not set" }, ...categoryOptions(type)]
        : categoryOptions(type);
  const category = type === "sell" ? item.part_type : categoryOf(item);
  const categoryField: FieldDef[] = categoryChoices.length
    ? [
        {
          key: "category",
          label: "Category",
          type: "select",
          options: categoryChoices,
          readOnly: sharedType && type !== "packaging",
          save: (v) =>
            v !== category &&
            mutate("item.update", {
              id: item.id,
              patch:
                type === "packaging"
                  ? packCategoryPatch(item, v as PackType | "")
                  : type === "sell"
                    ? { part_type: v as PartType }
                    : categoryPatch(item, String(v)),
            }),
        },
      ]
    : [];
  const local = (more: FieldDef[]): FieldDef[] => [
    { key: "tags", label: "Tags", type: "tags", suggestions: tagSuggestions },
    { key: "procurement", label: "Procurement", type: "select", options: options(PROCUREMENT_LABELS) },
    { key: "lifecycle", label: "Lifecycle", type: "select", options: options(LIFECYCLE_LABELS) },
    ...more,
    { key: "uom", label: "Unit", placeholder: "pcs" },
  ];
  // Shopify's fields save through Shopify (shopify.edit_variants), not item.update.
  const variant = (key: keyof VariantPatch, label: string, def: Partial<FieldDef> = {}): FieldDef => ({
    key,
    label,
    readOnly: !can("shopify.edit_variants"),
    save: (value) => saveVariant(item, { [key]: value } as VariantPatch),
    ...def,
  });
  const fields: FieldDef[] = shopify
    ? [
        variant("sku", "SKU", { mono: true }),
        variant("barcode", "Barcode", { mono: true }),
        variant("price", "Price", {
          type: "number",
          format: money,
          save: (value) => (value == null ? toast.error("Price can't be empty") : saveVariant(item, { price: value as number })),
        }),
        variant("compare_at_price", "Compare-at price", { type: "number", format: money }),
        variant("std_cost", "Cost", { type: "number", format: money }),
        variant("weight_g", "Weight (g)", { type: "number", format: (v) => (v == null ? "" : `${Math.round(Number(v) * 10) / 10} g`) }),
        variant("hs_code", "HS code", { mono: true }),
        variant("country_of_origin", "Country of origin", { placeholder: "e.g. CN" }),
        variant("shopify_tracked", "Track inventory", { type: "bool" }),
        project,
        ...local(categoryField),
        { key: "description", label: "Notes for us", type: "textarea" },
        { key: "notes", label: "Notes", type: "textarea" },
      ]
    : [
        { key: "name", label: "Name" },
        { key: "sku", label: "SKU", mono: true, prefix: skuPrefix(item) }, // Part SKUs --- the prefix is fixed, the rest edited
        typeField,
        ...categoryField,
        ...local([]),
        { key: "std_cost", label: "Standard cost", type: "number", format: money, placeholder: "USD per unit" },
        { key: "barcode", label: "Barcode", mono: true },
        { key: "weight_g", label: "Weight (g)", type: "number" },
        { key: "hs_code", label: "HS code", mono: true },
        { key: "country_of_origin", label: "Country of origin", placeholder: "e.g. CN" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "notes", label: "Notes", type: "textarea" },
        { key: "archived", label: "Archived", type: "bool" },
      ];
  const row = { ...item, type, category }; // the virtual Type and Category fields read these
  const admin = shopifyAdminUrl(item);
  const writeState = worst(writes.item.get(item.id), writes.product.get(productId(item)));

  return (
    <RecordPanel
      title={shopify && item.shopify_variant_title ? item.shopify_product_title : item.name}
      subtitle={[shopify ? item.shopify_variant_title : "", item.sku, pack ? pack.title : typeLabel(item)].filter(Boolean).join(" · ")}
      media={<PhotoSlot item={item} />}
      badges={
        <>
          {shopify && <Badge tone="accent">Shopify</Badge>}
          {item.lifecycle !== "active" && <Badge tone="warn">{LIFECYCLE_LABELS[item.lifecycle]}</Badge>}
          {item.archived && <Badge tone="warn">Archived</Badge>}
          {writeState === "pending" && <Badge tone="warn">Saving to Shopify…</Badge>}
          {writeState === "failed" && <Badge tone="danger">Shopify refused a change</Badge>}
          {writeState === "reverted" && <Badge tone="danger">Changed back in Shopify by another app</Badge>}
          {/* Labels and printouts --- */}
          <a
            href={`/api/labels/item/${item.id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-5 items-center gap-1 text-[11px] text-accent-text hover:underline"
            title="A 2.25 × 1.25 in label: SKU, name and a Code 128 barcode (bag labels: 4 × 2 in)"
          >
            Label
          </a>
          <a
            href={`/api/labels/item/${item.id}?kind=bag`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-5 items-center gap-1 text-[11px] text-accent-text hover:underline"
          >
            Bag label
          </a>
          {admin && (
            <a href={admin} target="_blank" rel="noreferrer" className="inline-flex h-5 items-center gap-1 text-[11px] text-accent-text hover:underline">
              Open in Shopify <ExternalLink className="size-3" />
            </a>
          )}
        </>
      }
      row={row}
      fields={fields}
      update="item.update"
      entity="item"
      onClose={close}
      top={
        <>
          <ItemSummary item={item} />
          {/* Part groups --- its siblings in other colours or models, what they share, adding one like it */}
          <div id="sec-group" data-sec="Group">
            <GroupSection item={item} />
          </div>
          {/* Packaging --- a packaging part's kind, printing and print files come first: they're what its vendor needs */}
          {pack && (
            <div id="sec-packaging" data-sec="Packaging" className="mb-2 border-b border-line pb-2">
              <PackagingSection item={item} />
            </div>
          )}
        </>
      }
      fieldsTitle={shopify ? "Shopify fields: SKU, barcode, price, cost, weight, project, tags, notes" : undefined}
    >
      {/* In the order the work goes: what to do (the plan), what's here, what's coming, who from, what sells. The chips
          at the top jump to each (ItemSummary.tsx). */}
      <div id="sec-plan" data-sec="Plan">
        <ItemPlanSection item={item} /> {/* Planning --- */}
      </div>
      <div id="sec-stock" data-sec="Stock">
        <StockList itemId={item.id} />
        <LocalStock item={item} /> {/* Receiving --- */}
      </div>
      <div id="sec-order" data-sec="On order">
        <OnOrder itemId={item.id} />
      </div>
      <div id="sec-received" data-sec="Received">
        <ItemReceipts itemId={item.id} /> {/* Cross-links --- every delivery of it, each opening its PO */}
      </div>
      <div id="sec-vendors" data-sec="Vendors">
        <VendorLinks item={item} />
        <PriceHistorySection item={item} /> {/* Production runs --- quotes and PO prices over time, against the plan */}
      </div>
      <div id="sec-sales" data-sec="Sales">
        <SalesSection item={item} />
      </div>
      <div id="sec-forecast" data-sec="Forecast">
        <ForecastSection item={item} /> {/* Forecasting --- */}
      </div>
      <div id="sec-bom" data-sec="BOM">
        <BomItemSection item={item} hideEmpty={boughtFinished} />
      </div>
      <div id="sec-spec" data-sec="Spec">
        <SpecSection item={item} hideEmpty={boughtFinished || (!!pack && !pack.spec)} /> {/* Specs --- */}
      </div>
      <div id="sec-board" data-sec="Board">
        <BoardSection item={item} /> {/* Board components --- from its spec's KiCad project, BOM or not */}
      </div>
      {boughtFinished && !specs.specByItem.has(item.id) && (
        <div className="mt-3 border-t border-line px-2 pt-2 text-[12px] text-faint">
          Bought finished, so no BOM or spec.{" "}
          <button type="button" className="text-accent-text hover:underline" onClick={() => setMakeSectionsFor(item.id)}>
            Add one
          </button>
        </div>
      )}
      <div id="sec-shopify" data-sec="Shopify product">
        {shopify && item.shopify_product_id && <ProductSection id={item.shopify_product_id} />}
        {shopify && item.shopify_variant_title && <Variants item={item} />}
      </div>
    </RecordPanel>
  );
}

function PhotoSlot({ item }: { item: Item }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const editable = item.source === "local" && can("item.update");

  const upload = async (file: Blob) => {
    setBusy(true);
    try {
      const url = await uploadPhoto(file, item.sku || item.name);
      await mutate("item.update", { id: item.id, patch: { photo_url: url } });
    } catch (e) {
      toast.error((e as Error).message);
    }
    setBusy(false);
  };

  // Paste a screenshot (Ctrl+V) anywhere while the item is open to make it the photo.
  useEffect(() => {
    if (!editable) return;
    const onPaste = (e: ClipboardEvent) => {
      if (isTyping(e.target)) return;
      const file = imageFrom(e.clipboardData);
      if (file) {
        e.preventDefault();
        upload(file);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  const box = "flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-line bg-surface-2";
  if (!editable) return item.photo_url ? <Thumb src={item.photo_url} size={64} className="border border-line" /> : <div className={box} />;
  return (
    <button
      type="button"
      title="Upload a photo (or paste one with Ctrl+V)"
      className={cx(box, "text-faint hover:border-accent hover:text-accent-text", busy && "animate-pulse")}
      onClick={() => input.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const file = imageFrom(e.dataTransfer);
        if (file) upload(file);
      }}
    >
      {item.photo_url ? <Thumb src={item.photo_url} size={64} /> : <ImagePlus className="size-5" />}
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
          e.target.value = "";
        }}
      />
    </button>
  );
}

/** What every variant of the product shares: title, status, vendor, type and Shopify tags. */
function ProductSection({ id }: { id: string }) {
  const product = useRow("shopify_product", id);
  const products = useRows("shopify_product");
  const suggestions = useMemo(() => [...new Set(products.flatMap((p) => p.tags))].sort(), [products]);
  if (!product) return null;
  const field = (key: keyof ProductPatch, label: string, def: Partial<FieldDef> = {}): FieldDef => ({
    key,
    label,
    readOnly: !can("shopify.edit_products"),
    save: (value) => saveProduct(id, { [key]: value } as ProductPatch),
    ...def,
  });
  return (
    <Section title={product.variant_count > 1 ? `Product · all ${product.variant_count} variants` : "Product"}>
      <div className="flex flex-col gap-0.5">
        <Fields
          row={product}
          fields={[
            field("title", "Title", { save: (value) => saveTitle(id, String(value ?? "")) }),
            field("status", "Status", { type: "select", options: STATUS_OPTIONS }),
            field("vendor", "Vendor"),
            field("product_type", "Product type"),
            field("tags", "Shopify tags", { type: "tags", keepCase: true, suggestions }),
          ]}
        />
      </div>
    </Section>
  );
}

const breaksText = (value: unknown) =>
  ((value as { min_qty: number; unit_price: number }[]) ?? []).map((b) => `${qty(b.min_qty)}+ @ ${money(b.unit_price)}`).join(", ");

/** The other variants of this item's Shopify product, to step between them. */
function Variants({ item }: { item: Item }) {
  const paneOpen = usePaneOpen(); // Side pane --- another variant, in place of this one
  const all = useRows("item");
  const stock = useRows("stock_level");
  const locations = useRows("location"); // Consignment --- HHL's stock only
  const siblings = useMemo(
    () =>
      all
        .filter((i) => i.shopify_product_id === item.shopify_product_id && !i.archived)
        .sort((a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true })),
    [all, item.shopify_product_id],
  );
  const atHhl = useMemo(() => hhlStock(stock, [], locations, untrackedIds(siblings)), [stock, locations, siblings]);
  const columns = useMemo<Column<Item>[]>(
    () => [
      {
        key: "variant",
        header: "Variant",
        width: "minmax(140px,2fr)",
        value: (v) => v.shopify_variant_title,
        cell: (v) => (
          <>
            <Thumb src={v.photo_url} size={20} />
            <span className="truncate">{v.shopify_variant_title}</span>
          </>
        ),
      },
      { key: "sku", header: "SKU", width: "minmax(100px,1fr)", cell: (v) => <span className="truncate font-mono text-[12px] text-muted">{v.sku}</span> },
      {
        key: "available",
        header: "Available",
        width: "90px",
        align: "right",
        value: (v) => atHhl.get(v.id)?.available ?? 0,
        cell: (v) =>
          v.shopify_tracked === false ? (
            <NotTracked />
          ) : (
            <span className="font-mono text-[12px] text-muted" title={`Available at HHL${stockNote(atHhl.get(v.id)) ? `: ${stockNote(atHhl.get(v.id))}` : ""}`}>
              {qty(atHhl.get(v.id)?.available ?? 0)}
            </span>
          ),
      },
    ],
    [atHhl],
  );
  return (
    <Section title="Variants" count={siblings.length}>
      <div className="px-2 pb-2">
        <DataTable
          fit
          rows={siblings}
          columns={columns}
          defaultSort={{ key: "sku" }}
          activeId={item.id}
          onOpen={(v) => paneOpen("item", v.id, true)}
          rowNoun={["variant", "variants"]}
          exportName="Variants"
          pageFilters={false}
        />
      </div>
    </Section>
  );
}

/** A vendor link's fields: its full form (VendorLinkDialog); the table's cells save the same way. */
const linkFields = (link: VendorItem): FieldDef[] => [
  { key: "vendor_sku", label: "Vendor SKU", mono: true },
  { key: "vendor_name", label: "Their name", placeholder: "Name on their quotes and POs" },
  { key: "unit_price", label: "Unit price", type: "number", format: (v) => money(v, link.currency) },
  { key: "moq", label: "MOQ", type: "number", format: qty },
  { key: "order_multiple", label: "Multiple", type: "number", format: qty, placeholder: "Reel, tray or carton size" },
  { key: "lead_time_days", label: "Lead time", type: "number", format: days, placeholder: "Days from order to ship" },
  { key: "quote_ref", label: "Quote", placeholder: "PI or quote number" },
  { key: "quote_expires", label: "Quote expires", type: "date" },
  { key: "notes", label: "Notes", type: "textarea" },
];

type LinkKey = "vendor_sku" | "vendor_name" | "unit_price" | "moq" | "order_multiple" | "lead_time_days" | "quote_ref" | "quote_expires" | "notes";

/**
 * The item's vendors, in the usual table (views, columns, Copy / CSV): the plain fields edit in their cells (as the form
 * saves them: a number or a sum, a date like 2026-12-31, empty clears), and a row opens its full form beside it (notes,
 * price breaks, what they say in the portal, Make preferred, Remove).
 */
function VendorLinks({ item }: { item: Item }) {
  const links = useRows("vendor_item").filter((l) => l.item_id === item.id);
  const vendors = useRows("vendor");
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const editable = can("vendor_item.update");
  const unlinked = vendors.filter((v) => !v.archived && !links.some((l) => l.vendor_id === v.id));
  const vendorOf = (l: VendorItem) => vendors.find((v) => v.id === l.vendor_id);
  const vendorName = (l: VendorItem) => {
    const v = vendorOf(l);
    return v ? v.short_name || v.name : "Unknown vendor";
  };
  const open = links.find((l) => l.id === openId);

  // A cell saves what its form field would: text as typed, a number (or a sum) or nothing, a date or nothing.
  const editor = (key: LinkKey, label: string, type?: "number" | "date") => ({
    can: () => editable,
    get: (l: VendorItem) => (l[key] == null ? "" : String(l[key])),
    save: (l: VendorItem, text: string) => {
      const t = text.trim();
      let value: unknown = text;
      if (type === "number") {
        value = t === "" ? null : calc(t);
        if (t !== "" && value == null) return toast.error(`${label}: enter a number`);
      } else if (type === "date") {
        if (t && !/^\d{4}-\d{2}-\d{2}$/.test(t)) return toast.error(`${label}: use a date like 2026-12-31`);
        value = t || null;
      }
      mutate("vendor_item.update", { id: l.id, patch: { [key]: value } });
    },
  });
  const columns: Column<VendorItem>[] = [
    {
      key: "vendor",
      header: "Vendor",
      width: "minmax(120px,1.2fr)",
      value: vendorName,
      cell: (l) => (
        <PaneLink kind="vendor" id={l.vendor_id} to={`/vendors?open=${l.vendor_id}`} className="truncate font-medium hover:underline">
          {vendorName(l)}
        </PaneLink>
      ),
    },
    {
      key: "preferred",
      header: "Preferred",
      width: "132px",
      value: (l) => (l.is_preferred ? 0 : 1),
      text: (l) => (l.is_preferred ? "yes" : ""),
      cell: (l) =>
        l.is_preferred ? (
          <Badge tone="ok">Preferred</Badge>
        ) : (
          editable && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Star className="size-3.5" />}
              onClick={(e) => {
                e.stopPropagation();
                mutate("vendor_item.update", { id: l.id, patch: { is_preferred: true } });
              }}
            >
              Make preferred
            </Button>
          )
        ),
    },
    {
      key: "vendor_sku",
      header: "Vendor SKU",
      width: "minmax(100px,1fr)",
      cell: (l) => <span className="truncate font-mono text-[12px]">{l.vendor_sku}</span>,
      edit: editor("vendor_sku", "Vendor SKU"),
    },
    {
      key: "vendor_name",
      header: "Their name",
      headerTitle: "Name on their quotes and POs",
      width: "minmax(120px,1.2fr)",
      optional: true,
      cell: (l) => (
        <span className="truncate" title={l.vendor_name}>
          {l.vendor_name}
        </span>
      ),
      edit: editor("vendor_name", "Their name"),
    },
    {
      key: "unit_price",
      header: "Unit price",
      width: "96px",
      align: "right",
      value: (l) => (l.unit_price == null ? null : Number(l.unit_price)),
      cell: (l) => <span className="font-mono text-[12px]">{money(l.unit_price, l.currency)}</span>,
      edit: editor("unit_price", "Unit price", "number"),
    },
    {
      key: "moq",
      header: "MOQ",
      width: "72px",
      align: "right",
      value: (l) => (l.moq == null ? null : Number(l.moq)),
      cell: (l) => <span className="font-mono text-[12px]">{qty(l.moq)}</span>,
      edit: editor("moq", "MOQ", "number"),
    },
    {
      key: "order_multiple",
      header: "Multiple",
      headerTitle: "Reel, tray or carton size",
      width: "80px",
      align: "right",
      value: (l) => (l.order_multiple == null ? null : Number(l.order_multiple)),
      cell: (l) => <span className="font-mono text-[12px]">{qty(l.order_multiple)}</span>,
      edit: editor("order_multiple", "Multiple", "number"),
    },
    {
      key: "lead_time_days",
      header: "Lead time",
      headerTitle: "Days from order to ship",
      width: "84px",
      align: "right",
      value: (l) => (l.lead_time_days == null ? null : Number(l.lead_time_days)),
      cell: (l) => <span className="font-mono text-[12px]">{days(l.lead_time_days)}</span>,
      edit: editor("lead_time_days", "Lead time", "number"),
    },
    {
      key: "price_breaks",
      header: "Price breaks",
      headerTitle: 'Typed as "1000 @ 0.65, 5000 @ 0.60"',
      width: "minmax(120px,1.2fr)",
      value: (l) => breaksText(l.price_breaks),
      cell: (l) => <span className="truncate font-mono text-[12px]">{breaksText(l.price_breaks)}</span>,
      edit: { can: () => editable, get: breaksDraft, save: (l, text) => void saveBreaks(l, text) },
    },
    {
      key: "quote_ref",
      header: "Quote",
      headerTitle: "PI or quote number",
      width: "minmax(90px,1fr)",
      optional: true,
      cell: (l) => <span className="truncate">{l.quote_ref}</span>,
      edit: editor("quote_ref", "Quote"),
    },
    {
      key: "quote_expires",
      header: "Quote expires",
      width: "110px",
      cell: (l) => <span className="text-muted">{l.quote_expires ?? ""}</span>,
      edit: editor("quote_expires", "Quote expires", "date"),
    },
    {
      key: "notes",
      header: "Notes",
      width: "minmax(120px,1.5fr)",
      optional: true,
      cell: (l) => (
        <span className="truncate text-muted" title={l.notes}>
          {l.notes}
        </span>
      ),
      // Notes of several lines are the form's to edit (a one-line cell would lose the lines)
      edit: { ...editor("notes", "Notes"), can: (l: VendorItem) => editable && !l.notes.includes("\n") },
    },
    {
      key: "portal_stock",
      header: "Their stock",
      headerTitle: "The stock they say they have for us, in the vendor portal",
      width: "96px",
      align: "right",
      optional: true,
      value: (l) => (l.vendor_stock == null ? null : Number(l.vendor_stock)),
      cell: (l) => <span className="font-mono text-[12px] text-muted">{l.vendor_stock == null ? "" : qty(l.vendor_stock)}</span>,
    },
    {
      key: "remove",
      header: "",
      width: "36px",
      value: () => "",
      cell: (l) =>
        can("vendor_item.remove") && (
          <button
            type="button"
            className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
            title="Remove this vendor from the item"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm(`Remove ${vendorOf(l)?.name ?? "this vendor"} from ${item.name}?`)) mutate("vendor_item.remove", { id: l.id });
            }}
          >
            <Trash2 className="size-3.5" />
          </button>
        ),
    },
  ];

  return (
    <Section title="Vendors" count={links.length} addLabel="Add vendor" onAdd={can("vendor_item.create") ? () => setAdding(true) : undefined}>
      {adding && (
        <div className="mx-2 mb-2 flex gap-2">
          <Select
            autoFocus
            defaultValue=""
            options={[{ value: "", label: "Choose a vendor…" }, ...unlinked.map((v) => ({ value: v.id, label: v.short_name || v.name }))]}
            onChange={async (e) => {
              if (!e.target.value) return;
              setAdding(false);
              await mutate("vendor_item.create", { id: crypto.randomUUID(), vendor_id: e.target.value, item_id: item.id, currency: "USD" });
            }}
            onKeyDown={(e) => e.key === "Escape" && setAdding(false)}
          />
          <Button variant="ghost" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      )}
      {!links.length && !adding && <div className="px-2 pb-2 text-muted">No vendor yet.</div>}
      {links.length > 0 && (
        <div className="px-2 pb-2">
          <DataTable
            fit
            rows={links}
            columns={columns}
            defaultSort={{ key: "preferred" }}
            editing={editable}
            activeId={openId}
            onOpen={(l) => setOpenId(l.id)}
            rowNoun={["vendor", "vendors"]}
            exportName="Item vendors"
            pageFilters={false}
          />
        </div>
      )}
      {open && <VendorLinkDialog link={open} item={item} vendorName={vendorName(open)} editable={editable} onClose={() => setOpenId(null)} />}
    </Section>
  );
}

/** One vendor link's full form, beside the row it was opened from: every field, its price breaks and the portal's side. */
function VendorLinkDialog({
  link,
  item,
  vendorName,
  editable,
  onClose,
}: {
  link: VendorItem;
  item: Item;
  vendorName: string;
  editable: boolean;
  onClose: () => void;
}) {
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex max-h-[84vh] w-[480px] max-w-[calc(100vw-32px)] flex-col rounded-xl border border-line bg-surface shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="flex items-center gap-2 border-b border-line px-3 py-2">
          <PaneLink kind="vendor" id={link.vendor_id} to={`/vendors?open=${link.vendor_id}`} className="font-medium hover:underline">
            {vendorName}
          </PaneLink>
          {link.is_preferred ? (
            <Badge tone="ok">Preferred</Badge>
          ) : (
            editable && (
              <Button
                size="sm"
                variant="ghost"
                icon={<Star className="size-3.5" />}
                onClick={() => mutate("vendor_item.update", { id: link.id, patch: { is_preferred: true } })}
              >
                Make preferred
              </Button>
            )
          )}
          {can("vendor_item.remove") && (
            <button
              type="button"
              className="ml-auto rounded p-1 text-faint hover:bg-hover hover:text-danger"
              title="Remove this vendor from the item"
              onClick={async () => {
                if (confirm(`Remove ${vendorName} from ${item.name}?`) && (await mutate("vendor_item.remove", { id: link.id }))) onClose();
              }}
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
          <button
            type="button"
            className={cx("rounded p-1 text-faint hover:bg-hover hover:text-text", !can("vendor_item.remove") && "ml-auto")}
            title="Close (Esc)"
            onClick={onClose}
          >
            <X className="size-3.5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          <Fields compact row={link} update="vendor_item.update" disabled={!editable} fields={linkFields(link)} />
          <PriceBreaks link={link} disabled={!editable} />
          <VendorSide link={link} />
        </div>
      </div>
    </DialogLayer>
  );
}

/** A link's price breaks as they're typed ("1000 @ 0.65, 5000 @ 0.6"). */
const breaksDraft = (link: VendorItem) =>
  ((link.price_breaks as { min_qty: number; unit_price: number }[]) ?? []).map((b) => `${b.min_qty} @ ${b.unit_price}`).join(", ");

/** Saves price breaks typed as "1000 @ 0.65, 5000 @ 0.60" (nothing typed clears them). */
async function saveBreaks(link: VendorItem, draft: string) {
  const parts = draft
    .split(/[,;\n]/)
    .map((p) => p.trim())
    .filter(Boolean);
  const breaks = parts.map((p) => {
    const m = p.replace(/[$,]/g, "").match(/^(\d+(?:\.\d+)?)\s*\+?\s*(?:@|=|:|\s)\s*(\d*\.?\d+)$/);
    return m ? { min_qty: Number(m[1]), unit_price: Number(m[2]) } : null;
  });
  if (breaks.some((b) => !b)) return toast.error('Write price breaks like "1000 @ 0.65, 5000 @ 0.60"');
  await mutate("vendor_item.update", {
    id: link.id,
    patch: { price_breaks: (breaks as { min_qty: number; unit_price: number }[]).sort((a, b) => a.min_qty - b.min_qty) },
  });
}

/** Price breaks typed as "1000 @ 0.65, 5000 @ 0.6" (the quickest way to copy them off a quote). */
function PriceBreaks({ link, disabled }: { link: VendorItem; disabled: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const save = async () => {
    setEditing(false);
    await saveBreaks(link, draft);
  };
  return (
    <div className={cx("flex rounded-md px-2", !disabled && "hover:bg-hover")}>
      <div className="w-24 shrink-0 pt-1.5 text-muted">Price breaks</div>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              e.stopPropagation(); // puts the box back, not the form it's in away
              setEditing(false);
            }
          }}
          placeholder="1000 @ 0.65, 5000 @ 0.60"
          className="my-0.5 h-8 flex-1 rounded-md border border-accent bg-surface px-2.5 font-mono text-[12px] focus:outline-none"
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setDraft(breaksDraft(link));
            setEditing(true);
          }}
          className="min-h-8 flex-1 rounded-md px-2.5 py-1.5 text-left font-mono text-[12px]"
        >
          {breaksText(link.price_breaks) || <span className="font-sans text-faint">{disabled ? "—" : "e.g. 1000 @ 0.65, 5000 @ 0.60"}</span>}
        </button>
      )}
    </div>
  );
}

function StockList({ itemId }: { itemId: string }) {
  const levels = useRows("stock_level").filter((s) => s.item_id === itemId);
  const locations = useRows("location");
  const item = useRow("item", itemId);
  if (!levels.length) return null;
  // A labor SKU Shopify doesn't track: the counts Shopify keeps for it mean nothing
  if (item?.shopify_tracked === false)
    return (
      <Section title="Stock">
        <div className="px-4 pb-2 text-[12px] text-muted">Not tracked in Shopify, so no stock: the counts Shopify keeps for it mean nothing.</div>
      </Section>
    );
  return (
    <Section title="Stock" count={levels.length}>
      <div className="px-2 pb-2">
        {levels.map((s) => (
          <div key={s.id} className="flex justify-between rounded-md px-2 py-1 hover:bg-hover">
            <span>{locations.find((l) => l.id === s.location_id)?.name ?? "Unknown location"}</span>
            <span className="font-mono text-[12px]">
              <span className={cx(Number(s.available) < 0 && "text-danger")}>{qty(s.available)} available</span>
              {Number(s.available) !== Number(s.on_hand) && (
                <span className="text-muted">
                  {" "}
                  · {qty(s.on_hand)} on hand{Number(s.committed) > 0 && ` · ${qty(s.committed)} committed to customer orders`}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}
